import { Head, Link, router } from "@inertiajs/react"
import { Download, FileText, Pencil, Trash2 } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import { formatDate, formatDateTime } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface DocumentDetail {
  id: number
  bg_document_type_id: number | null
  document_type: string | null
  title: string | null
  notes: string | null
  filename: string | null
  filetype: string | null
  content_type: string | null
  directory: string | null
  document_date: string | null
  width: number | null
  height: number | null
  user_name: string | null
  date_added: string | null
  significant: number
  on_reports: number
  is_public: number
  thumbnail_filename: string | null
  status: number
  has_file: boolean
}

interface Props {
  case_record: CaseRecord
  document: DocumentDetail
}

export default function Show({ case_record, document: doc }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Documents", href: `/cases/${case_record.id}/documents` },
    {
      title: doc.title || doc.filename || `Document #${doc.id}`,
      href: `/cases/${case_record.id}/documents/${doc.id}`,
    },
  ]

  const basePath = `/cases/${case_record.id}/documents`
  const downloadUrl = `${basePath}/${doc.id}/download`

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this document?")) {
      router.delete(`${basePath}/${doc.id}`)
    }
  }

  const filetypeLabel = doc.filetype?.toUpperCase() || "FILE"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Document - ${case_record.case_number}`} />

      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">
              {doc.title || doc.filename || `Document #${doc.id}`}
            </h1>
            {doc.significant === 1 && (
              <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                Significant
              </span>
            )}
            {doc.on_reports === 1 && (
              <span className="bg-hpi-light-blue text-hpi-dark-blue rounded px-2 py-0.5 text-xs font-medium">
                On Reports
              </span>
            )}
            {doc.is_public === 1 && (
              <span className="bg-hpi-light-green text-hpi-dark-green rounded px-2 py-0.5 text-xs font-medium">
                Public
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {doc.has_file && (
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 inline-flex items-center gap-1.5 rounded px-4 py-2 text-sm text-white"
              >
                <Download className="h-4 w-4" />
                View File
              </a>
            )}
            <Link
              href={`${basePath}/${doc.id}/edit`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue inline-flex items-center gap-1.5 rounded px-4 py-2 text-sm text-white"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className="bg-hpi-red hover:bg-hpi-red/80 inline-flex items-center gap-1.5 rounded px-4 py-2 text-sm text-white"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Main content */}
          <div className="space-y-6 md:col-span-2">
            {/* File card */}
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">File</h2>
              <div className="border-hpi-light-gray flex items-center gap-4 rounded border p-4">
                <div className="bg-hpi-pale-blue text-hpi-dark-blue flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                  <FileText className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  {doc.has_file ? (
                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-hpi-active-blue truncate text-sm font-medium hover:underline"
                    >
                      {doc.filename || "Download"}
                    </a>
                  ) : (
                    <span className="text-hpi-med-gray text-sm">
                      {doc.filename || "No file attached"}
                    </span>
                  )}
                  <div className="text-hpi-med-gray mt-0.5 text-xs">
                    {[filetypeLabel, doc.content_type]
                      .filter(Boolean)
                      .join(" — ")}
                  </div>
                </div>
                {doc.has_file && (
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-hpi-active-blue hover:text-hpi-dark-blue shrink-0"
                  >
                    <Download className="h-5 w-5" />
                  </a>
                )}
              </div>
            </div>

            {/* Notes card */}
            {doc.notes && (
              <div className="rounded-lg bg-white p-6 shadow">
                <h2 className="mb-3 text-lg font-semibold">Notes</h2>
                <div className="text-hpi-dark-gray text-sm whitespace-pre-wrap">
                  {doc.notes}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div>
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">Details</h2>
              <dl className="space-y-3 text-sm">
                {doc.document_type && (
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">Type</dt>
                    <dd className="font-medium">{doc.document_type}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Document Date</dt>
                  <dd>{formatDate(doc.document_date)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Uploaded By</dt>
                  <dd>{doc.user_name || "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Date Added</dt>
                  <dd>{formatDateTime(doc.date_added)}</dd>
                </div>
                {doc.width != null && doc.height != null && (
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">Dimensions</dt>
                    <dd>
                      {doc.width} x {doc.height}
                    </dd>
                  </div>
                )}

                <div className="border-hpi-light-gray my-3 border-t" />

                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Significant</dt>
                  <dd>{doc.significant === 1 ? "Yes" : "No"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">On Reports</dt>
                  <dd>{doc.on_reports === 1 ? "Yes" : "No"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Public</dt>
                  <dd>{doc.is_public === 1 ? "Yes" : "No"}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Link
            href={`/cases/${case_record.id}/documents`}
            className="text-hpi-active-blue text-sm hover:underline"
          >
            Back to Documents
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
